import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { Sparkles, HelpCircle } from "lucide-react";

const EMPTY = {
  id: "",
  name: "",
  url: "",
  profile: "",
  profile_type: "browser",
  prompt_selector: "",
  submit_selector: "",
  response_selector: "",
  response_ready_selector: "",
  notes: "",
};

const PRESETS = [
  {
    label: "Gemini Web",
    values: {
      id: "gemini",
      name: "Google Gemini",
      url: "https://gemini.google.com/app",
      profile: "gemini",
      profile_type: "browser",
      prompt_selector: 'div[contenteditable="true"], rich-textarea textarea',
      submit_selector: 'button[aria-label*="Enviar" i], button[aria-label*="Send" i]',
      response_selector: "message-content:last-of-type, .model-response-text:last-of-type",
      response_ready_selector: 'message-actions[data-response-complete], [data-turn-complete="true"]',
    },
  },
  {
    label: "ChatGPT",
    values: {
      id: "chatgpt",
      name: "ChatGPT",
      url: "https://chat.openai.com/",
      profile: "chatgpt",
      profile_type: "browser",
      prompt_selector: 'textarea, div[contenteditable="true"]',
      submit_selector: 'button[data-testid="send-button"]',
      response_selector: '[data-message-author-role="assistant"]:last-of-type',
      response_ready_selector: 'button[data-testid="regenerate-button"]',
    },
  },
];

export default function AddAiModelDialog({ open, onOpenChange, editing = null, onSaved }) {
  const isEdit = Boolean(editing);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit && editing) {
      setForm({ ...EMPTY, ...editing });
    } else {
      setForm(EMPTY);
    }
  }, [open, isEdit, editing]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function applyPreset(p) {
    setForm((f) => ({ ...f, ...p.values, notes: f.notes }));
    toast.success(`Preset "${p.label}" aplicado`);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.id.trim() || !form.name.trim() || !form.url.trim()) {
      toast.error("id, nombre y URL son obligatorios");
      return;
    }
    if (!form.profile.trim()) {
      toast.error("Perfil de navegador/sesión obligatorio");
      return;
    }
    if (!form.prompt_selector || !form.submit_selector || !form.response_selector) {
      toast.error("Selectores prompt/submit/response son obligatorios");
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const { id, ...patch } = form;
        const { data } = await api.patch(`/ai/models/${encodeURIComponent(editing.id)}`, patch);
        saved = data;
      } else {
        const { data } = await api.post("/ai/models", form);
        saved = data;
      }
      toast.success(isEdit ? "Modelo actualizado" : "Modelo creado");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.aiModels.dialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[640px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {isEdit ? `Editar modelo — ${editing.id}` : "Nuevo modelo IA"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            KRAI API automatiza el chat con este modelo usando el perfil de
            navegador que indiques. Tus apps mandan prompts y reciben respuestas
            vía <code className="font-mono">POST /api/ai/models/{"{id}"}/prompt</code>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-md border border-zinc-800 bg-black/30">
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-300 hover:text-white"
            >
              <HelpCircle className="h-3.5 w-3.5 text-zinc-400" />
              ¿Cómo funciona? {showHelp ? "▾" : "▸"}
            </button>
            {showHelp && (
              <div className="px-4 pb-4 pt-1 text-[12px] text-zinc-400 leading-relaxed space-y-2">
                <p>
                  Este módulo <b className="text-zinc-200">no llama a APIs de IA</b>.
                  Abre la web del modelo con la sesión ya iniciada (perfil
                  <code className="font-mono text-zinc-300"> gemini</code> u otro),
                  escribe tu prompt en el textarea, pulsa enviar, espera la
                  respuesta y te la devuelve como JSON.
                </p>
                <p>
                  <b className="text-zinc-200">Perfil</b>: nombre del perfil que
                  creaste en <b>Navegador / Playwright</b> (o en Sesiones). Ahí
                  vive el <code className="font-mono">storage_state.json</code>.
                </p>
                <p>
                  <b className="text-zinc-200">Selectores CSS</b>: se obtienen con
                  botón derecho → Inspeccionar → Copy selector, sobre cada
                  elemento de la web del modelo (textarea, botón enviar, contenedor
                  de la respuesta).
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono self-center mr-1">
              Presets:
            </span>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p)}
                className="text-xs rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 px-3 py-1"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ID (único)">
              <Input
                data-testid={TID.aiModels.fId}
                disabled={isEdit}
                value={form.id}
                onChange={(e) => set("id", e.target.value)}
                placeholder="gemini"
                pattern="[a-zA-Z0-9_\-]+"
                maxLength={64}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </Field>
            <Field label="Nombre visible">
              <Input
                data-testid={TID.aiModels.fName}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Google Gemini"
                className="bg-zinc-900 border-zinc-800 text-sm"
              />
            </Field>
          </div>

          <Field label="URL del modelo">
            <Input
              data-testid={TID.aiModels.fUrl}
              value={form.url}
              onChange={(e) => set("url", e.target.value)}
              placeholder="https://gemini.google.com/app"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Field label="Perfil (Navegador o Sesión)">
                <Input
                  data-testid={TID.aiModels.fProfile}
                  value={form.profile}
                  onChange={(e) => set("profile", e.target.value)}
                  placeholder="gemini"
                  pattern="[a-zA-Z0-9_\-]+"
                  className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                />
              </Field>
            </div>
            <Field label="Tipo de perfil">
              <Select value={form.profile_type} onValueChange={(v) => set("profile_type", v)}>
                <SelectTrigger
                  data-testid={TID.aiModels.fProfileType}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="browser" className="focus:bg-zinc-900">Navegador</SelectItem>
                  <SelectItem value="session" className="focus:bg-zinc-900">Sesión</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="rounded-md border border-zinc-800 bg-black/40 p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              Selectores CSS del chat
            </div>
            <Field label="Textarea del prompt">
              <Input
                data-testid={TID.aiModels.fPromptSel}
                value={form.prompt_selector}
                onChange={(e) => set("prompt_selector", e.target.value)}
                placeholder='textarea   |   div[contenteditable="true"]'
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </Field>
            <Field label="Botón enviar">
              <Input
                data-testid={TID.aiModels.fSubmitSel}
                value={form.submit_selector}
                onChange={(e) => set("submit_selector", e.target.value)}
                placeholder='button[aria-label="Enviar"]'
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </Field>
            <Field label="Contenedor de la respuesta (última)">
              <Input
                data-testid={TID.aiModels.fRespSel}
                value={form.response_selector}
                onChange={(e) => set("response_selector", e.target.value)}
                placeholder='.model-response-text:last-of-type'
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </Field>
            <Field label="Selector 'respuesta completada' (opcional)">
              <Input
                data-testid={TID.aiModels.fReadySel}
                value={form.response_ready_selector}
                onChange={(e) => set("response_ready_selector", e.target.value)}
                placeholder='[data-turn-complete="true"]'
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </Field>
          </div>

          <Field label="Notas">
            <Textarea
              data-testid={TID.aiModels.fNotes}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-sm"
            />
          </Field>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid={TID.aiModels.submit}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear modelo"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300">{label}</Label>
      {children}
    </div>
  );
}
